import { useMutation, useQuery } from '@tanstack/react-query'
import { CataloguesFilterParams, CreateCatalogueParams, UpdateCatalogueParams } from '@/types/catalogue'
import { useCataloguesStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useCatalogues = (params: CataloguesFilterParams) => {
  const fetchCatalogues = useCataloguesStore(state => state.fetchCatalogues)

  return useQuery({
    queryKey: ['catalogues', params],
    queryFn: () => fetchCatalogues(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useCatalogue = (id: string) => {
  const getCatalogue = useCataloguesStore(state => state.getCatalogue)

  return useQuery({
    queryKey: ['catalogue', id],
    queryFn: () => getCatalogue(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateCatalogue = () => {
  const createCatalogue = useCataloguesStore(state => state.createCatalogue)

  return useMutation({
    mutationFn: (params: CreateCatalogueParams) => createCatalogue(params),
    onSuccess: () => {}
  })
}

export const useUpdateCatalogue = () => {
  const updateCatalogue = useCataloguesStore(state => state.updateCatalogue)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateCatalogueParams }) => updateCatalogue(id, params),
    onSuccess: () => {}
  })
}

export const useSortCatalogue = () => {
  const sortCatalogue = useCataloguesStore(state => state.sortCatalogue)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortCatalogue(id, type),
    onSuccess: () => {}
  })
}

export const useDeleteCatalogue = () => {
  const deleteCatalogue = useCataloguesStore(state => state.deleteCatalogue)

  return useMutation({
    mutationFn: (id: string) => deleteCatalogue(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteCatalogues = () => {
  const bulkDeleteCatalogues = useCataloguesStore(state => state.bulkDeleteCatalogues)
  const deselectAllCatalogues = useCataloguesStore(state => state.deselectAllCatalogues)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteCatalogues(ids),
    onSuccess: response => {
      deselectAllCatalogues()
      handleBulkDeleteSuccess(response)
    }
  })
}
